import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { publicUrl } from '../../config/index.js';
import { getDeviceId } from '../../lib/identity.js';
import {
  createGroupSync,
  GroupJoinError,
  groupProgress,
  isSessionJoinable,
  liveVoteCounts,
  placeSummary,
  readGroupState,
  restaurantFromPlace,
} from './sync/index.js';

const EMPTY_STATE = readGroupState(null);
const EMPTY_VOTES = {};

/**
 * Owns everything about the active group session: membership, live vote state,
 * and the derived leaderboard.
 *
 * Only one session can be active at a time, so this is deliberately a single
 * hook rather than context — `DiscoverScreen` is the one consumer.
 *
 * @param {import('../../lib/types.js').Restaurant[]} catalogue
 */
export function useGroupSession(catalogue) {
  const [code, setCode] = useState(null);
  const [state, setState] = useState(EMPTY_STATE);
  const [isRealtime, setIsRealtime] = useState(false);
  const [resolvedRestaurants, setResolvedRestaurants] = useState([]);

  // Two ids, because they are not always the same thing. `deviceId` is this
  // browser; `memberId` is who this browser is *in the current session*, which
  // differs when a guest claims one of the names the host invited them under.
  // Votes key off the session identity, so that is the one used everywhere
  // below and the one exported.
  const deviceId = useRef(getDeviceId()).current;
  const [memberId, setMemberId] = useState(deviceId);
  const syncRef = useRef(null);
  const unsubscribeRef = useRef(null);

  /** Tears down listeners without touching remote state. */
  const detach = useCallback(() => {
    unsubscribeRef.current?.();
    unsubscribeRef.current = null;
    syncRef.current = null;
  }, []);

  useEffect(() => detach, [detach]);

  /**
   * Creates or joins a session and starts streaming its state.
   *
   * Joining validates first: without `create`, the code must resolve to a live
   * session, otherwise a typo would silently spin up a brand-new empty group
   * (the bug this guards against). Creating skips the check by design.
   *
   * @param {string} sessionCode
   * @param {string} displayName
   * @param {{ create?: boolean, as?: string }} [options]  `as` is the member key to
   *   join under when the guest has claimed one of the session's invited names,
   *   so their votes land against the id the host already knows them by.
   * @throws {GroupJoinError} when joining a code that doesn't exist or has ended.
   */
  const connect = useCallback(
    async (sessionCode, displayName, { create = false, as, invites, deck } = {}) => {
      detach();
      setResolvedRestaurants([]);
      const identity = as ?? deviceId;

      const sync = await createGroupSync(sessionCode);

      if (!create) {
        const snapshot = await sync.snapshot();
        if (!isSessionJoinable(snapshot)) {
          throw new GroupJoinError(snapshot == null ? 'not-found' : 'expired');
        }
      }

      await sync.join({ memberId: identity, name: displayName || 'Guest' });

      // Names the people this session was opened for, so whoever follows the
      // invite can claim their own name and keep the group's history. Mirrors
      // the mobile hook; only the host sends it, and only when there are names
      // to send — a group started from scratch has none.
      if (create && invites?.length) await sync.invite(invites);
      if (create && deck) await sync.setDeck(deck);

      syncRef.current = sync;
      unsubscribeRef.current = sync.subscribe(setState);

      setCode(sessionCode);
      setMemberId(identity);
      setIsRealtime(sync.isRealtime);

      return sync;
    },
    [detach, deviceId]
  );

  /**
   * Watches a session without joining it — how someone opening an invite after
   * the group has ended still gets to see what it picked.
   *
   * @param {string} sessionCode
   */
  const observe = useCallback(
    async (sessionCode) => {
      detach();
      const sync = await createGroupSync(sessionCode);
      syncRef.current = sync;
      unsubscribeRef.current = sync.subscribe(setState);
      setCode(sessionCode);
      setIsRealtime(sync.isRealtime);
    },
    [detach]
  );

  const progress = useMemo(() => groupProgress(state), [state]);
  const hasSubmitted = state.submitted[memberId] != null;
  const myVotes = state.votes[memberId] ?? EMPTY_VOTES;

  /** Leaves the session, removing this member's votes for everyone else. */
  const leave = useCallback(async () => {
    // Once a group has ended its votes are the answer everyone is looking at,
    // so stepping away only detaches.
    if (!progress.ended) await syncRef.current?.leave({ memberId });
    detach();
    setCode(null);
    setState(EMPTY_STATE);
    setResolvedRestaurants([]);
    // Back to being this browser rather than whoever was claimed.
    setMemberId(deviceId);
  }, [detach, deviceId, memberId, progress.ended]);

  /**
   * Records this member's vote.
   *
   * Local state is updated first so the UI never waits on the network; the
   * subscription later overwrites it with the authoritative value.
   *
   * @param {import('../../lib/types.js').Restaurant} restaurant
   * @param {'left'|'right'} direction
   * @param {string|null} [time]
   */
  const recordVote = useCallback(
    (restaurant, direction, time = null) => {
      // Submitted votes are final, and an ended group takes no more.
      if (hasSubmitted || progress.ended) return;

      setState((current) => ({
        ...current,
        votes: {
          ...current.votes,
          [memberId]: { ...(current.votes[memberId] ?? {}), [restaurant.id]: direction },
        },
        times: time
          ? {
              ...current.times,
              [memberId]: { ...(current.times[memberId] ?? {}), [restaurant.id]: time },
            }
          : current.times,
      }));

      syncRef.current?.recordVote({
        memberId,
        restaurantId: restaurant.id,
        direction,
        time,
        // Google Places content stays in memory; only its Place ID is stored in
        // the deck selection. Catalogue-owned rows may keep their summary.
        place: direction === 'right' && !restaurant.googlePlaceId ? placeSummary(restaurant) : null,
      });
    },
    [memberId, hasSubmitted, progress.ended]
  );

  /** Marks this member finished after every restaurant in the ballot is swiped. */
  const submitVotes = useCallback(async () => {
    if (progress.ended) return;
    setState((current) => ({
      ...current,
      submitted: { ...current.submitted, [memberId]: Date.now() },
    }));
    await syncRef.current?.submit({ memberId });
  }, [memberId, progress.ended]);

  /** Closes voting for everyone. Offered to whoever started the group. */
  const endGroup = useCallback(async () => {
    if (progress.ended) return;
    await syncRef.current?.end({ memberId });
  }, [memberId, progress.ended]);

  // The group finishes itself once everyone in it has submitted. Whichever
  // member's client notices first writes it; the write is idempotent.
  const isMember = state.members[memberId] != null;
  useEffect(() => {
    if (!code || !isMember || progress.ended || !progress.allSubmitted) return;
    syncRef.current?.end({ memberId }).catch(() => {});
  }, [code, isMember, memberId, progress.ended, progress.allSubmitted]);

  const voteCounts = useMemo(() => liveVoteCounts(state), [state]);

  /** Adds live Google group cards to the in-memory leaderboard resolver. */
  const resolveRestaurants = useCallback((restaurants) => {
    setResolvedRestaurants(restaurants.filter((restaurant) => restaurant.googlePlaceId));
  }, []);

  /**
   * Restaurants with at least one vote, best first, each annotated with who
   * voted for it and the time that most of them chose.
   */
  const leaderboard = useMemo(() => {
    const byId = new Map(
      [...catalogue, ...resolvedRestaurants].map((restaurant) => [
        String(restaurant.id),
        restaurant,
      ])
    );
    return Object.entries(voteCounts)
      .map(([id, votes]) => {
        const place = state.places[id];
        const restaurant = byId.get(id) ?? (place ? restaurantFromPlace(place) : null);
        return restaurant ? { restaurant, votes } : null;
      })
      .filter(Boolean)
      .sort((a, b) => b.votes - a.votes)
      .map((entry) => {
        const voters = Object.entries(state.members)
          .filter(([id]) => state.votes[id]?.[entry.restaurant.id] === 'right')
          .map(([id, member]) => ({
            id,
            name: member.name || 'Guest',
            time: state.times[id]?.[entry.restaurant.id] ?? null,
          }));

        return { ...entry, voters, bestTime: mostCommonTime(voters) };
      });
  }, [
    catalogue,
    resolvedRestaurants,
    voteCounts,
    state.members,
    state.votes,
    state.times,
    state.places,
  ]);

  const inviteUrl = code ? `${publicUrl}/?group=${code}` : null;

  return {
    code,
    memberId,
    isRealtime,
    isActive: Boolean(code),
    members: state.members,
    deckSelection: state.deck,
    memberCount: progress.memberCount,
    submittedCount: progress.submittedCount,
    hasSubmitted,
    myVotes,
    isEnded: progress.ended,
    voteCounts,
    leaderboard,
    topPick: leaderboard[0] ?? null,
    inviteUrl,
    connect,
    observe,
    leave,
    recordVote,
    resolveRestaurants,
    submitVotes,
    endGroup,
  };
}

/**
 * The time the most voters picked. Ties resolve to whichever appears first,
 * which is good enough for a suggestion.
 *
 * @param {{time: string | null}[]} voters
 * @returns {string | null}
 */
function mostCommonTime(voters) {
  /** @type {Record<string, number>} */
  const frequency = {};
  for (const voter of voters) {
    if (!voter.time) continue;
    frequency[voter.time] = (frequency[voter.time] ?? 0) + 1;
  }

  const ranked = Object.entries(frequency).sort((a, b) => b[1] - a[1]);
  return ranked[0]?.[0] ?? null;
}
